import {useContext, useMemo, useCallback} from "react";
import {useQuery, useQueries} from "@tanstack/react-query";
import {API} from "api";
import {Images} from "contexts/images";
import {
  useResolvedDefaultScene,
  useResolvedDefaultScenesAll,
  useResolvedDefaultScenesBatch,
  resolvedDefaultScenesAllQueryOptions,
} from "hooks/useResolvedDefaultScenes";
import {
  catalogHasStoredTransforms,
  catalogTransformForTemplate,
  channelRendersOwnBackgrounds,
  computeEffectiveBgTransform,
  legacyDefaultBgTransform,
  effectiveBgOptionKey,
  splitAllProductsTemplateId,
  templateShowsCatalogImage,
} from "libs/bgTransform";
import {
  authoritativeSceneData,
  isProductDefaultAuthoritative,
  sceneTransformLayerForOption,
} from "libs/authoritativeDefaultScene";
import {isScopesChannel} from "libs/sceneScope";
import {tileBaseColor} from "libs/tileBase";
import {BG_PREVIEW_TEMPLATE_FIELD} from "libs/bgPreviewTemplate";
import {shouldUseLivestreamResolvedTransforms} from "pages/Livestream/productBackgrounds";
import {getReleaseProductCodesForType} from "utils/releases";

/**
 * Phase-2 background-transform derivation hooks (issue #528, 2a).
 *
 * React Query is the ONLY cache for the fetched layers (catalog + resolved
 * scene) — per the no-shadow-state rule they're never mirrored into the images
 * context. `useEffectiveBgTransform` composes the displayed transform from user
 * intent (context) + those queries by fixed precedence, so a late fetch fills
 * its slot instead of clobbering state (deletes the three-writer race).
 *
 * `enabled` controls the canvas crop. `previewsEnabled` lets saved scenes
 * load gallery defaults while keeping their stored canvas crop.
 */

/** GET /api/v3/backgrounds — the v3 catalog (1a). */
export function useBackgroundCatalog({channel, type, product, enabled = true}) {
  return useQuery({
    queryKey: ["v3-backgrounds", channel, type, product ?? null],
    queryFn: () => API.fetchBackgroundsCatalog(channel, type, product),
    enabled: Boolean(enabled && channel && type),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Selector: returns `getEffectiveTransform(templateId, background?)` computing
 * the displayed crop by precedence (userAdjustment -> resolvedScene -> [ALL
 * only: that product's own resolved scene] -> catalog -> the legacy
 * DEFAULT_BACKGROUND_DATA table for a variation with no stored scene -> null; a
 * null means the template has no crop at all). `background` is the parent route's
 * bgOption; in ALL-products mode each child banner displays its own carousel
 * variation, so per-variation layers resolve under the option the banner shows
 * (see effectiveBgOptionKey) — `perProductBg` is the carousel state. When
 * `enabled` is false the canvas selector yields null. Gallery previews can
 * load independently through `previewsEnabled`.
 */
export function useEffectiveBgTransform({
  enabled,
  previewsEnabled = enabled,
  channel,
  type,
  product,
  set,
  layout,
  background,
  variant,
  perProductBg,
  locked,
  catalogBackgrounds,
  extraBackgrounds = null,
}) {
  // The channel, the displayed bgOption and customBg/customBgs decide whether the
  // catalog layer applies at all — a template rendering its own baked default or
  // an upload isn't showing the catalog's artwork, so the catalog's crop doesn't
  // describe it (templateShowsCatalogImage; `locked` is the setting-state half of
  // that mirror). Gating here rather than in BgTransformer covers the export path
  // too: App composes the saved scene from this same selector
  // (composeSceneBgTransforms), so a display-only fix would still have baked the
  // wrong crop into every render.
  const {userBgAdjustments, customBg, customBgs, customBgUploaded} = useContext(Images);

  const readDefaults = enabled || previewsEnabled;
  const catalogQuery = useBackgroundCatalog({channel, type, product, enabled: readDefaults});
  const resolvedQuery = useResolvedDefaultScene({
    channel,
    product,
    type,
    set,
    layout,
    background,
    // Same key as useInitialScene resolves: one cache entry, one cascade.
    variant,
    enabled: readDefaults,
  });
  // A successful HTTP response can still report a failed scene-store read.
  const productDefaultError = useMemo(() => {
    const data = resolvedQuery.data;
    return data?.product_default_error && isProductDefaultAuthoritative(data, product)
      ? new Error(`Default scene couldn't be loaded: ${data.product_default_error}`)
      : null;
  }, [resolvedQuery.data, product]);

  // ALL-products ("What's New"): every banner is one product's instance of a
  // template, and neither the catalog nor the ALL row resolves a crop for all of
  // them (#567, R4) — the catalog because `sources.ALL` doesn't exist, the ALL row
  // because its scoped `<template>-<product>` keys only cover the products that
  // existed when an admin last saved it. Each product's OWN resolved scene is the
  // missing layer; the query is the one useInitialScene already runs for the
  // aggregate, so React Query serves it from cache.
  const isAllProducts = product === "ALL";
  const allProductsQuery = useResolvedDefaultScenesAll({
    channel,
    type,
    set,
    layout,
    background,
    enabled: enabled && isAllProducts,
  });
  // The products whose `<template>-<product>` instance ids the ALL view draws.
  // A page of one product needs them too, to know such an id is no banner of it.
  const productCodes = useMemo(
    () => (channel === "releases" ? getReleaseProductCodesForType(type) : []),
    [channel, type]
  );

  // Every variation the child carousels currently display beyond the parent's —
  // each needs its own /resolved-all resolution for the productScene layer (a
  // per-product override row can be background-scoped). Distinct values only:
  // 30 banners typically share one or two variations, and React Query dedupes
  // against useInitialScene's parent-background query anyway.
  const childBackgrounds = useMemo(() => {
    if (!isAllProducts) return [];
    const uniq = new Set();
    for (const value of Object.values(perProductBg ?? {})) {
      if (value !== undefined && value !== null) uniq.add(String(value));
    }
    uniq.delete(String(background));
    return [...uniq].sort();
  }, [isAllProducts, perProductBg, background]);

  const childScenes = useQueries({
    queries: childBackgrounds.map((bg) => ({
      ...resolvedDefaultScenesAllQueryOptions({channel, type, set, layout, background: bg}),
      enabled: Boolean(enabled && isAllProducts && channel && type),
    })),
    combine: combineChildScenes,
  });

  // The catalog layer is keyed by the value `bgOption` holds, which is a
  // POSITION — normally the same number as the doc's `variation`. Under the
  // editable background list they part company (a reorder moves an image to a
  // different position), so App passes the resolved list in and its entries,
  // whose `variation` field already carries the position, key the map instead.
  const catalogByVariation = useMemo(() => {
    const map = {};
    for (const b of catalogBackgrounds ?? catalogQuery.data?.backgrounds ?? []) {
      map[String(b.variation)] = b;
    }
    // Temporary upload tiles (libs/sessionBgUploads.ts) carry their own crop.
    for (const b of extraBackgrounds ?? []) map[String(b.variation)] = b;
    return map;
  }, [catalogBackgrounds, catalogQuery.data, extraBackgrounds]);

  // The scene layer (resolvedScene) is NOT keyed by the displayed variation the
  // way the layers below are: on an ALL route its identity is the parent route
  // state — its scoped `<template>-<product>` crops restore an admin-saved layout
  // regardless of carousel position, exactly as the legacy fan-out resolved every
  // product at the parent's background (useInitialScene's
  // aggregateAllProductsFields).
  //
  // Which scene that is, is NOT `scene_data`: it's whichever half of the resolved
  // response useInitialScene applies — the cascade, or the route's products.js
  // scene when the cascade resolved nothing (or for KED products, which the
  // wildcard rows would otherwise leak IDE crops onto). Reading only the cascade
  // rendered KED release banners at the IDE crop with the rotation dropped, and
  // left every products.js-only route (webinar) deriving from the catalog alone
  // (#567). The decision is shared with useInitialScene so the two can't
  // disagree; only the products.js half is variation-gated (see
  // sceneTransformLayerForOption).
  const productScenesByBg = useMemo(() => {
    const map = {[String(background)]: allProductsQuery.data?.scenes ?? null};
    childBackgrounds.forEach((bg, i) => {
      map[bg] = childScenes[i] ?? null;
    });
    return map;
  }, [background, allProductsQuery.data, childBackgrounds, childScenes]);

  // The background selector draws every tile with the crop the tile's own
  // background gets. The scene layer is keyed by background, so each tile
  // needs its own resolution; one batch request serves the whole list. Off for
  // ALL-products (each banner there shows its own product's image) and for the
  // channels that never show the catalog artwork.
  const showsTilePreviews = !isAllProducts && !channelRendersOwnBackgrounds(channel);
  const tileBackgrounds = useMemo(
    () =>
      showsTilePreviews
        ? Object.keys(catalogByVariation).sort((a, b) => Number(a) - Number(b))
        : [],
    [showsTilePreviews, catalogByVariation]
  );
  const tileScenesQuery = useResolvedDefaultScenesBatch({
    channel,
    product,
    type,
    set,
    layout,
    variant,
    backgrounds: tileBackgrounds,
    enabled: previewsEnabled && showsTilePreviews,
  });

  // The precedence chain for one template under one displayed option, given
  // the resolved response that belongs to it.
  const deriveTransform = useCallback(
    (templateId, optionKey, instance, resolvedData, showsCatalogImage) => {
      const catalogItem = catalogByVariation[optionKey];
      const baseTemplateId = instance?.templateId ?? templateId;
      // Layer 5 applies only where legacy applied it: a (product, variation)
      // with no transform scene at all, on a template that is showing the
      // catalog artwork. With a scene present, a template the scene omits stays
      // at identity — legacy's `coords || DEFAULT_BACKGROUND_DATA` is
      // all-or-nothing per variation, never per template.
      const legacyDefault =
        showsCatalogImage && !catalogHasStoredTransforms(catalogItem, instance?.productCode)
          ? legacyDefaultBgTransform(baseTemplateId)
          : undefined;
      const resolvedScene = sceneTransformLayerForOption(resolvedData, product, optionKey);
      const productScene = instance
        ? productScenesByBg[optionKey]?.[instance.productCode]
        : undefined;
      // A crop follows its image (libs/cropFollowsImage.ts). When the product
      // gives the banner an image of its own (a ready cut), the ALL row's crop
      // of the instance was made for another image: the product's scene alone
      // places this one, as on the product's own page.
      const productGivesImage = Boolean(productScene?.customBgs?.[baseTemplateId]);
      const useResolvedScene =
        !productGivesImage &&
        (channel !== "livestream" ||
          shouldUseLivestreamResolvedTransforms(product, resolvedData?.layers, templateId));
      return computeEffectiveBgTransform({
        userAdjustment: userBgAdjustments?.[optionKey]?.[templateId],
        resolvedScene: useResolvedScene ? resolvedScene : undefined,
        productScene,
        baseTemplateId: instance?.templateId,
        catalogTransform: showsCatalogImage
          ? catalogTransformForTemplate(catalogItem, baseTemplateId, instance?.productCode)
          : undefined,
        legacyDefault,
        templateId,
      });
    },
    [channel, product, catalogByVariation, productScenesByBg, userBgAdjustments]
  );

  const getEffectiveTransform = useCallback(
    (templateId, explicitBackground) => {
      if (!enabled) return null;
      // In ALL mode the banner's own product decides both scene and crop; the
      // instance id is split back into (template, product) to look either up.
      const instance =
        productCodes.length > 0 ? splitAllProductsTemplateId(templateId, productCodes) : null;
      // A page of one product draws no instance. Its `visible` and its scene
      // still carry the ALL view's instance keys from the "*" rows, and the
      // server reads the product's own key onto the template id
      // (materializeAllProductsWhatsNewFields). Read as a banner here, that key
      // went into a default save as the product's own crop, and the crop then
      // moved the product's ready cut (RM What's New, 2026-10-20).
      if (instance && !isAllProducts) return null;
      // The variation this banner is displaying: BgTransformer passes its own
      // provider's bgOption; the buildScene composition passes nothing and
      // falls back to the carousel state, then the parent's option.
      const optionKey = effectiveBgOptionKey({
        explicitBackground,
        instance,
        perProductBg,
        parentBackground: background,
      });
      const showsCatalogImage = templateShowsCatalogImage({
        channel,
        templateId,
        bgOption: optionKey,
        customBg,
        customBgs,
        customBgUploaded,
        locked,
      });
      return deriveTransform(
        templateId,
        optionKey,
        instance,
        resolvedQuery.data,
        showsCatalogImage
      );
    },
    [
      enabled,
      background,
      perProductBg,
      resolvedQuery.data,
      productCodes,
      isAllProducts,
      deriveTransform,
      channel,
      customBg,
      customBgs,
      customBgUploaded,
      locked,
    ]
  );

  // The banners' crops derive from the layers above. Until the layers of the
  // key on screen have loaded, every banner derives no crop: a scene built
  // then carries none, and a default saved then writes empty crop records over
  // the saved ones. A new layout or background is a new key.
  const transformsReady =
    !enabled ||
    (resolvedQuery.data !== undefined &&
      (catalogBackgrounds ?? catalogQuery.data) !== undefined &&
      (!isAllProducts || allProductsQuery.data !== undefined));

  // The tile layers are ready when the scenes of every tile have loaded. The
  // current background reads the banners' own query, so its tile and its
  // banners always agree.
  const tileTransformsReady = Boolean(
    previewsEnabled && showsTilePreviews && resolvedQuery.data && tileScenesQuery.data
  );

  /**
   * The crop `templateId` gets when the selector's tile `variation` is picked.
   * Undefined while the tile layers load or where tiles show no preview; null
   * means the template has no crop (natural size, top-left).
   */
  const tileResolvedData = useCallback(
    (optionKey) =>
      optionKey === String(background) ? resolvedQuery.data : tileScenesQuery.data?.[optionKey],
    [background, resolvedQuery.data, tileScenesQuery.data]
  );

  // A saved scene uses these defaults only after a gallery selection.
  const getTileDefaults = useCallback(
    (variation) => {
      if (!tileTransformsReady) return undefined;
      const scene = authoritativeSceneData(tileResolvedData(String(variation)), product);
      const background = catalogByVariation[String(variation)];
      return {
        scene,
        imageUrl: background?.imageUrl,
        base: tileBaseColor({scene, catalogBase: background?.base}),
      };
    },
    [tileTransformsReady, tileResolvedData, product, catalogByVariation]
  );

  // Every per-banner image in a default scene is a preset, including images
  // written by an importer. Read it from this tile's scene, not the live tile.
  const getTileImage = useCallback(
    (templateId, variation) => {
      if (!tileTransformsReady) return undefined;
      const scene = authoritativeSceneData(tileResolvedData(String(variation)), product);
      return scene?.customBgs?.[templateId] || null;
    },
    [tileTransformsReady, tileResolvedData, product]
  );

  const getTileTransform = useCallback(
    (templateId, variation) => {
      if (!tileTransformsReady) return undefined;
      const optionKey = String(variation);
      const resolvedData = tileResolvedData(optionKey);
      // Each tile uses its own image state. The selected background's presets
      // must not disable the catalog crop of the other tiles.
      const showsCatalogImage = !getTileImage(templateId, variation);
      return deriveTransform(templateId, optionKey, null, resolvedData, showsCatalogImage);
    },
    [tileTransformsReady, tileResolvedData, deriveTransform, getTileImage]
  );

  /**
   * The banner the saved default scene of tile `variation` names for its
   * preview (`bgPreviewTemplate`), or null. Undefined while the tile layers
   * load.
   */
  const getTileSavedPreviewTemplate = useCallback(
    (variation) => {
      if (!tileTransformsReady) return undefined;
      const optionKey = String(variation);
      const scene = sceneTransformLayerForOption(tileResolvedData(optionKey), product, optionKey);
      const id = scene?.[BG_PREVIEW_TEMPLATE_FIELD];
      return typeof id === "string" && id ? id : null;
    },
    [tileTransformsReady, tileResolvedData, product]
  );

  /**
   * The banners whose image tile `variation` takes from a row of the current
   * layout: the layout's own artwork (FigBaker's `NN_bg_MM_layout` frames).
   * The tile previews one of them, so the tile changes with the layout.
   * Undefined while the tile layers load.
   */
  const getTileLayoutImages = useCallback(
    (variation) => {
      if (!tileTransformsReady) return undefined;
      const resolvedData = tileResolvedData(String(variation));
      const scene = authoritativeSceneData(resolvedData, product);
      // A products.js scene draws the tile: no row applies.
      if (!layout || !scene || scene !== resolvedData?.scene_data) return [];
      const ids = new Set();
      for (const layer of resolvedData.layers ?? []) {
        if (String(layer?.layout ?? "") !== String(layout)) continue;
        for (const [id, url] of Object.entries(layer?.scene_data?.customBgs ?? {})) {
          // Only where this row's image is the one the banner shows.
          if (url && scene.customBgs?.[id] === url) ids.add(id);
        }
      }
      return [...ids];
    },
    [tileTransformsReady, tileResolvedData, product, layout]
  );

  /**
   * The base colour `templateId` gets when the selector's tile `variation` is
   * picked, from that background's own default scene. Undefined while the
   * tile layers load.
   */
  const getTileBase = useCallback(
    (templateId, variation) => {
      if (!tileTransformsReady) return undefined;
      const optionKey = String(variation);
      return tileBaseColor({
        scene: authoritativeSceneData(tileResolvedData(optionKey), product),
        catalogBase: catalogByVariation[optionKey]?.base,
        templateId,
        perBanner: isScopesChannel(channel),
      });
    },
    [tileTransformsReady, tileResolvedData, product, catalogByVariation, channel]
  );

  return {
    getEffectiveTransform,
    transformsReady,
    getTileImage,
    getTileTransform,
    getTileSavedPreviewTemplate,
    getTileLayoutImages,
    getTileBase,
    getTileDefaults,
    tileTransformsReady,
    tileError: catalogQuery.error ?? resolvedQuery.error ?? tileScenesQuery.error ?? productDefaultError,
    retryTiles: () => Promise.all([catalogQuery.refetch(), resolvedQuery.refetch(), tileScenesQuery.refetch()]),
  };
}

// Module-level so useQueries' combine cache holds (a new function identity per
// render would bust it). replaceEqualDeep keeps the array referentially stable
// across renders, which keeps getEffectiveTransform — and with it every
// BgTransformer subscribed through the context — from re-deriving on unrelated
// state.
function combineChildScenes(results) {
  return results.map((r) => r.data?.scenes ?? null);
}
